import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { axiosInstance, API, AUTH_URL } from "../../utils/api";

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authError, setAuthError] = useState('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          const response = await axiosInstance.get(`${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.data) {
            navigate('/dashboard');
            return;
          }
        }
      } catch (e) {
        localStorage.removeItem('access_token');
      }
    };

    const handleSession = async () => {
      const hash = location.hash;
      if (hash && hash.includes('session_id=')) {
        setIsProcessing(true);
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          await axiosInstance.post(`${API}/auth/session-data`, {}, {
            headers: { 'X-Session-ID': sessionId }
          });
          
          window.history.replaceState({}, document.title, '/dashboard');
          navigate('/dashboard');
        } catch (error) {
          toast.error('Authentication failed');
          setIsProcessing(false);
        }
      } else {
        checkAuth();
      }
    };

    handleSession();
  }, [location, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `${AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthError('');

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const payload = authMode === 'login' 
        ? { email, password }
        : { email, password, name };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      if (authMode === 'login') {
        toast.success('Logged in successfully!');
        navigate('/dashboard');
      } else {
        toast.success('Account created successfully!');
        navigate('/select-plan');
      }
    } catch (error) {
      setAuthError(error.response?.data?.detail || 'Authentication failed');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      setForgotPasswordSuccess(true);
      toast.success('Password reset request submitted! Contact admin for reset token.');
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  if (isProcessing) {
    return (
      <div className="loading-spinner" data-testid="loading-spinner">
        Authenticating...
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="landing-page">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <img 
            src="/cxrecon-logo-new.png" 
            alt="CXrecon Logo" 
            className="hero-logo"
            data-testid="hero-logo"
          />
          <h2 className="hero-tagline-bold" data-testid="hero-tagline-bold">
            STOP GUESSING. START RECONNING.
          </h2>
          <p className="hero-description">
            Ground Truth Wins.
            <br /><br />
            Welcome to CX/recon: The easily deployed AI-powered intelligence platform that replaces fractured feedback with actionable, closed-loop insights.
            <br /><br />
            Stop collecting data, start gathering intelligence. Leverage CX/recon to map your internal and external customer battlefield in real-time. Uncover the tactical data your competitors miss, secure your perimeter, and ensure victory with engagement aware, process enhancing, revenue-driving CX improvements.
            <br /><br />
            Your mission starts now.
          </p>
          <button 
            className="cta-button cta-button-hero" 
            onClick={() => setShowAuthModal(true)}
            data-testid="hero-cta-button"
          >
            <span className="chevron-left">››</span>
            LAUNCH OPERATION GROUND TRUTH
            <span className="chevron-right">‹‹</span>
          </button>
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <div className="features-content">
          <h1 className="hero-title" data-testid="hero-title">
            AI-Powered Experience Management
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Create intelligent surveys in seconds. Analyze responses with AI. 
            Deliver actionable insights that transform customer and employee experiences.
          </p>
          
          <div className="container">
            <h2 className="reconnaissance-title">The Reconnaissance Toolkit: Unrivaled Intelligence</h2>
            
            <div className="feature-grid">
              <div className="feature-card">
                <h3>CX Vitals: AI-Driven Diagnostics</h3>
                <p>Instantly cut through noise. Our proprietary **Text iQ** analyzes open-ended responses for sentiment and hidden themes, providing the critical health metrics needed to predict churn.</p>
              </div>
             
              <div className="feature-card">
                <h3>Unified Intelligence Feed</h3>
                <p>Integrate with your mission-critical systems (Salesforce, Zendesk). Close the loop instantly by automatically turning a low CSAT score into a high-priority support ticket or Slack alert.</p>
              </div>

              <div className="feature-card">
                <h3>Omni-Channel Command</h3>
                <p>Launch your mission from every front: web intercepts, email, QR codes, and **offline kiosk mode**. Collect intelligence where your customer is, not just where it's easy.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advantage Section */}
      <div className="advantage-section">
        <div className="advantage-content">
          <div className="container">
            <h2 className="tactical-edge-title">The Tactical Edge: Why We Win</h2>
            
            <div className="feature-grid">
              <div className="advantage-item">
                <h3>Ground Truth, Guaranteed</h3>
                <p>We use AI Response Quality Filtering to ensure you're never basing strategy on rushed, fraudulent, or junk feedback. Only act on reliable data.</p>
              </div>
              <div className="advantage-item">
                <h3>The Commander's Console</h3>
                <p>Forget clunky reports. Customizable, real-time dashboards show you the **exact action** to take next to move your key CX and business metrics.</p>
              </div>
              <div className="advantage-item">
                <h3>Loyalty Lens Focus</h3>
                <p>Every feature is geared toward identifying the true drivers of customer **Loyalty** and predicting **Churn**, turning feedback into revenue.</p>
              </div>
            </div>
          </div>
          
          <div className="auth-buttons">
            <button 
              className="cta-button" 
              onClick={() => setShowAuthModal(true)}
              data-testid="email-login-button"
            >
              <span className="chevron-left">››</span>
              SECURE YOUR ACCESS
              <span className="chevron-right">‹‹</span>
            </button>
            <p className="beta-access-text">
              Beta Access limited to 100 Elite Operatives for this deployment.
            </p>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <div className="footer-section">
        <div className="footer-content">
          <p className="footer-copyright">
            CX/recon &copy; 2025. A Strategic Advantage.
          </p>
          <p className="footer-links">
            Privacy Policy | Terms of Service | Mission Briefing
          </p>
          <p className="footer-support">
            Combat Support: General@cxrecon.com
          </p>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal-header">
              <h2>{authMode === 'login' ? 'Sign In' : 'Create Account'}</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowAuthModal(false)}
                data-testid="close-auth-modal"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleEmailAuth} className="auth-form">
              {authMode === 'register' && (
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="name-input"
                  />
                </div>
              )}
              
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="6"
                  data-testid="password-input"
                />
              </div>
              
              {authMode === 'login' && (
                <div className="forgot-password-link">
                  <button
                    type="button"
                    className="forgot-password-btn"
                    onClick={() => {
                      setShowAuthModal(false);
                      setShowForgotPasswordModal(true);
                    }}
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              
              {authError && (
                <div className="auth-error" data-testid="auth-error">
                  {authError}
                </div>
              )}
              
              <button 
                type="submit" 
                className="auth-submit-btn"
                data-testid="auth-submit-button"
              >
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>
            
            <div className="auth-switch">
              {authMode === 'login' ? (
                <p>
                  Don't have an account?{' '}
                  <button 
                    type="button"
                    className="auth-switch-btn"
                    onClick={() => {
                      setAuthMode('register');
                      setAuthError('');
                    }}
                    data-testid="switch-to-register"
                  >
                    Sign up
                  </button>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <button 
                    type="button"
                    className="auth-switch-btn"
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError('');
                    }}
                    data-testid="switch-to-login"
                  >
                    Sign in
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="modal-overlay" onClick={() => {
          setShowForgotPasswordModal(false);
          setForgotPasswordSuccess(false);
          setForgotPasswordEmail('');
        }}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal-header">
              <h2>Reset Password</h2>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowForgotPasswordModal(false);
                  setForgotPasswordSuccess(false);
                  setForgotPasswordEmail('');
                }}
                data-testid="close-forgot-password-modal"
              >
                ✕
              </button>
            </div>
            
            {!forgotPasswordSuccess ? (
              <form onSubmit={handleForgotPassword} className="auth-form">
                <p className="forgot-password-info">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email-input"
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="auth-submit-btn"
                  data-testid="forgot-password-submit"
                >
                  Send Reset Link
                </button>
              </form>
            ) : (
              <div className="forgot-password-success">
                <p>✅ Password reset link sent!</p>
                <p>Check your email inbox (and spam folder) for a link to reset your password. The link expires in 1 hour.</p>
                <button 
                  className="auth-submit-btn"
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setForgotPasswordSuccess(false);
                    setForgotPasswordEmail('');
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
